import { useState } from 'react';
import { Phone, MessageCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { whatsappLink } from './leads.api';
import { BRAND } from '@/brand';

/**
 * Click-to-call and WhatsApp for a lead.
 *
 * Both press-and-forget buttons also LOG the contact against the lead (`POST /leads/:id/contact`),
 * because the log is the only evidence anyone actually reached out — a dialled call that leaves no
 * trace is indistinguishable from a lead nobody touched, which is the argument every pipeline
 * review gets stuck on.
 */

interface Props {
  leadId: number;
  mobile?: string | null;
  company?: string | null;
  contactName?: string | null;
  onDone?: () => void;
  size?: 'sm' | 'md';
}

export function LeadCallButton({ leadId, mobile, onDone, size = 'sm' }: Props) {
  const [busy, setBusy] = useState(false);
  if (!mobile) return null;
  const sz = size === 'sm' ? 'p-1' : 'px-2 py-1';

  const handle = async (e: React.MouseEvent) => {
    e.stopPropagation(); e.preventDefault();
    setBusy(true);
    try {
      // The same IVR bridge the order screens dial through: it rings the sales person first, then
      // the lead, so nobody's personal number is exposed.
      const r = await api.post('/ivr/call', { toNumber: mobile, reason: `Lead #${leadId}` });
      if (r.data?.ok) {
        toast.success(`Calling ${mobile}`);
        await api.post(`/leads/${leadId}/contact`, { type: 'CALL' });
      } else {
        throw new Error(r.data?.providerResponse ?? 'The calling system did not accept the request');
      }
    } catch (err: any) {
      // IVR not configured / gateway down is a normal Tuesday. Rather than dead-ending, hand the
      // number to whatever the machine dials with and record that the attempt was made by hand.
      const msg = err?.response?.data?.message ?? err?.message ?? 'Could not place the call';
      toast.error(`${msg} — dialling on this device instead`);
      try {
        window.location.href = `tel:${String(mobile).replace(/\s+/g, '')}`;
        await api.post(`/leads/${leadId}/contact`, { type: 'CALL', notes: 'dialled manually' });
      } catch { /* the toast already told them */ }
    } finally {
      setBusy(false);
      onDone?.();
    }
  };

  return (
    <button onClick={handle} disabled={busy} title={`Call ${mobile}`}
      className={`inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 ${sz}`}>
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Phone size={12} />}
    </button>
  );
}

export function LeadWhatsappButton({ leadId, mobile, company, contactName, onDone, size = 'sm' }: Props) {
  const link = whatsappLink(
    mobile,
    `Hello ${contactName || ''}${contactName ? ',' : ''} this is ${BRAND.name} regarding shipping rates for ${company ?? 'your business'}.`.replace(/\s+/g, ' ').trim(),
  );
  if (!link) return null;
  const sz = size === 'sm' ? 'p-1' : 'px-2 py-1';

  const handle = async (e: React.MouseEvent) => {
    e.stopPropagation(); e.preventDefault();
    // Opened first, then logged: a popup blocker that eats the window must not also lose the log,
    // and the log failing must not stop the message being sent.
    window.open(link, '_blank', 'noopener');
    try { await api.post(`/leads/${leadId}/contact`, { type: 'WHATSAPP' }); } catch { /* non-fatal */ }
    onDone?.();
  };

  return (
    <button onClick={handle} title={`WhatsApp ${mobile}`}
      className={`inline-flex items-center gap-1 rounded border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 ${sz}`}>
      <MessageCircle size={12} />
    </button>
  );
}

/** Both buttons together — the pair used in every list row. */
export function LeadContactButtons(p: Props) {
  if (!p.mobile) return <span className="text-gray-300">—</span>;
  return (
    <span className="inline-flex items-center gap-1">
      <LeadCallButton {...p} />
      <LeadWhatsappButton {...p} />
    </span>
  );
}
